import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/shared/api';
import type { AvailabilityConflict, AvailabilityResult } from '@/shared/api';

interface UseAvailabilityArgs {
  propertyId: string;
  from: string;
  to: string;
  enabled: boolean;
}

interface UseAvailabilityReturn {
  /** Für diesen Zeitraum steht noch keine Antwort aus – auch direkt nach einem Wechsel. */
  isLoading: boolean;
  data: AvailabilityResult | undefined;
  hardConflicts: AvailabilityConflict[];
  softConflicts: AvailabilityConflict[];
  /**
   * Die Verfügbarkeit konnte nicht ermittelt werden (Netzwerk, 403 auf die Channex-Fläche, …).
   * Muss propagiert werden: ohne Antwort sind beide Konfliktlisten leer, und ein Formular, das
   * nur auf `length === 0` prüft, gäbe die Buchung stillschweigend frei statt zu sperren.
   */
  isUnavailable: boolean;
  /** Ohne Verbindung wartet die Prüfung; sie gilt dann als ungeprüft, nicht als „frei“. */
  isOffline: boolean;
  error: unknown;
  /** Neue Prüfung, z. B. nach „Erneut prüfen“. */
  retry: () => void;
}

export function useAvailability({
  propertyId,
  from,
  to,
  enabled,
}: UseAvailabilityArgs): UseAvailabilityReturn {
  const api = useApi();
  const query = useQuery<AvailabilityResult>({
    queryKey: ['channex', 'availability', propertyId, from, to],
    queryFn: () => api.channex.checkAvailability(propertyId, from, to),
    enabled,
    staleTime: 30_000,
  });
  const hardConflicts = query.data?.conflicts.filter((c) => c.severity === 'Hard') ?? [];
  const softConflicts = query.data?.conflicts.filter((c) => c.severity === 'Soft') ?? [];
  const offline = enabled && query.data === undefined && query.fetchStatus === 'paused';
  return {
    isLoading: enabled && query.data === undefined && !query.isError && !offline,
    data: query.data,
    hardConflicts,
    softConflicts,
    isUnavailable: enabled && (query.isError || offline),
    isOffline: offline,
    error: query.error,
    retry: () => void query.refetch(),
  };
}
